import {
  HttpBackend,
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BYPASS_LOG } from '@core/interceptors/loading-screen.interceptor';
import { SKIP_ERROR_INTERCEPTOR } from '@core/interceptors/error.interceptor';
import { environment } from 'environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpService } from 'services/http.service';
import { CurriculumExtractionResult } from 'services/models/recruitment/curriculum-extraction-result.model';
import { RecruitmentCandidatePositionModel } from 'services/models/recruitment/recruitment-candidate-position.model';
import { RecruitmentCandidateWithPositionModel } from 'services/models/recruitment/recruitment-candidate-with-position.model';
import { RecruitmentCandidateModel } from 'services/models/recruitment/recruitment-candidate.model';
import {
  RecruitmentCloseStagePayload,
  RecruitmentReopenStagePayload,
} from 'services/models/recruitment/recruitment-pipeline-stage-actions.model';
import { RecruitmentReassignmentDetailsModel } from 'services/models/recruitment/recruitment-reassignment-details.model';
import { RecruitmentReassignmentInviteModel } from 'services/models/recruitment/recruitment-reassignment-invite.model';
import { RecruitmentReassignmentPreviewModel } from 'services/models/recruitment/recruitment-reassignment-preview.model';
import { RecruitmentModel } from 'services/models/recruitment/recruitment.model';
import { ScheduleInterviewPayload } from 'services/models/recruitment/schedule-interview.model';
import { ServiceBase } from 'services/servicebase';
import { TalentBankCandidatesPage } from 'services/models/recruitment/talent-bank-candidates-page.model';

export interface GetRecruitmentCandidateOptions {
  bypassLoading?: boolean;
  rethrowOnError?: boolean;
}

@Injectable()
export class RecruitmentCandidateService extends ServiceBase<RecruitmentCandidateModel> {
  private httpClientNoInterceptors: HttpClient;

  constructor(
    http: HttpService,
    private httpClient: HttpClient,
    handler: HttpBackend
  ) {
    super('recruitmentCandidate', http, environment.apiUrl);
    this.httpClientNoInterceptors = new HttpClient(handler);
  }

  createEntity(input: any): RecruitmentCandidateModel {
    return RecruitmentCandidateModel.create(input);
  }

  getCandidates(recruitmentId: string): Observable<RecruitmentCandidateWithPositionModel[]> {
    const url = `${environment.apiUrl}/${this.endpoint}/${recruitmentId}/candidates`;

    return this.httpClient
      .get<any>(url, {
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map((response: any) => {
          const data = response.data || response.body || response || [];
          return data.map((item: any) => this.createCandidateWithPosition(item, recruitmentId));
        }),
        catchError(error => {
          return of([]);
        })
      );
  }

  getCandidateByEmail(email: string): Observable<RecruitmentCandidateModel | null> {
    const normalizedEmail = (email || '').trim();

    if (!normalizedEmail) {
      return of(null);
    }

    const params = new HttpParams().set('email', normalizedEmail);

    return this.httpClientNoInterceptors
      .get<any>(`${environment.apiUrl}/${this.endpoint}/candidates/by-email`, {
        params,
      })
      .pipe(
        map((response: any) => {
          const payload = response?.data || response?.body || response;

          if (!payload) {
            return null;
          }

          if (Array.isArray(payload)) {
            if (!payload.length) {
              return null;
            }

            return RecruitmentCandidateModel.create(payload[0]);
          }

          return RecruitmentCandidateModel.create(payload);
        }),
        catchError(error => {
          if (error?.status === 404) {
            return of(null);
          }

          throw error;
        })
      );
  }

  getRecruitmentCandidate(
    candidateId: string,
    options: GetRecruitmentCandidateOptions = {}
  ): Observable<RecruitmentCandidateModel | null> {
    if (!candidateId) {
      throw new Error('CandidateId é obrigatório');
    }

    const url = `${this.baseUrl}/${this.endpoint}/candidates/${candidateId}`;
    const context = this.buildCandidateRequestContext(options);

    return this.httpClient.get<any>(url, context ? { context } : undefined).pipe(
      map((body: any) => {
        const data = body?.data ?? body?.body ?? body;
        if (!data) {
          return null;
        }
        return RecruitmentCandidateModel.create(data);
      }),
      catchError(error => (options.rethrowOnError ? throwError(error) : of(null)))
    );
  }

  private buildCandidateRequestContext(
    options: GetRecruitmentCandidateOptions
  ): HttpContext | undefined {
    if (!options.bypassLoading && !options.rethrowOnError) {
      return undefined;
    }

    const context = new HttpContext();
    if (options.bypassLoading) {
      context.set(BYPASS_LOG, true);
    }
    if (options.rethrowOnError) {
      context.set(SKIP_ERROR_INTERCEPTOR, true);
    }
    return context;
  }

  filterCandidates(
    recruitmentId: string,
    odataParams?: any
  ): Observable<{ candidates: RecruitmentCandidateModel[]; totalCount: number }> {
    let params = new HttpParams();

    if (odataParams) {
      Object.keys(odataParams).forEach(key => {
        if (odataParams[key] !== null && odataParams[key] !== undefined) {
          params = params.set(key, odataParams[key].toString());
        }
      });
    }

    const url = `${environment.apiUrl}/${this.endpoint}/${recruitmentId}/candidates/filter`;

    return this.httpClient
      .get<any>(url, {
        params,
        observe: 'response',
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map(response => {
          const data = response.body || [];
          const totalCount = parseInt(response.headers.get('X-Count') || '0', 10);

          const candidates = data.map((item: any) => RecruitmentCandidateModel.create(item));

          return {
            candidates,
            totalCount,
          };
        }),
        catchError(error => {
          return of({ candidates: [], totalCount: 0 });
        })
      );
  }

  submitCandidateApplication(
    data: RecruitmentCandidateModel | any,
    recruitmentId?: string
  ): Observable<any> {
    const payload =
      data instanceof RecruitmentCandidateModel ? data.serializeForApplication() : { ...data };

    if (recruitmentId) {
      payload.recruitmentId = recruitmentId;
    }

    return this.httpClientNoInterceptors.post(
      `${environment.apiUrl}/${this.endpoint}/page/apply-candidate`,
      payload
    );
  }

  getPreSignedUrlForCurriculum(
    recruitmentId: string,
    fileName: string,
    contentType: string
  ): Observable<{ url: string; fileKey: string }> {
    const params = {
      fileType: 'CURRICULUM',
      fileName: fileName,
      contentType: contentType,
      recruitmentId: recruitmentId,
    };

    return this.httpClientNoInterceptors.get<{ url: string; fileKey: string }>(
      `${environment.apiUrl}/${this.endpoint}/page/apply-candidate/pre-signed-url-put`,
      { params }
    );
  }

  getPreSignedUrlForPhoto(
    recruitmentId: string,
    fileName: string,
    contentType: string
  ): Observable<{ url: string; fileKey: string }> {
    const params = {
      fileType: 'PHOTO',
      fileName: fileName,
      contentType: contentType,
      recruitmentId: recruitmentId,
    };

    return this.httpClientNoInterceptors.get<{ url: string; fileKey: string }>(
      `${environment.apiUrl}/${this.endpoint}/page/apply-candidate/pre-signed-url-put`,
      { params }
    );
  }

  extractCurriculumFromResume(
    recruitmentId: string,
    fileKey: string
  ): Observable<CurriculumExtractionResult> {
    const params = {
      recruitmentId,
      fileKey,
    };

    return this.httpClientNoInterceptors.get<CurriculumExtractionResult>(
      `${environment.apiUrl}/${this.endpoint}/page/apply-candidate/extract-curriculum`,
      { params }
    );
  }

  getTalentBankCandidates(companyGroupId: string): Observable<RecruitmentCandidateModel[]> {
    if (!companyGroupId) {
      throw new Error('CompanyGroupId é obrigatório');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/talent-bank/candidates?companyGroupId=${companyGroupId}`;

    return this.httpClient
      .get<any>(url, {
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map((response: any) => {
          const data = response.value || response.data || response.body || response || [];
          return data.map((item: any) => {
            const candidate = RecruitmentCandidateModel.create(item);
            Object.keys(item).forEach(key => {
              (candidate as any)[key] = item[key];
            });
            return candidate;
          });
        }),
        catchError(error => {
          return of([]);
        })
      );
  }

  getTalentBankCandidatesOData(
    companyGroupId: string,
    odataParams: string
  ): Observable<TalentBankCandidatesPage> {
    if (!companyGroupId) {
      throw new Error('CompanyGroupId é obrigatório');
    }

    const base = `${environment.apiUrl}/${this.endpoint}/talent-bank/candidates?companyGroupId=${encodeURIComponent(companyGroupId)}`;
    const suffix = odataParams?.trim() ? `&${odataParams.trim().replace(/^\?/, '')}` : '';

    return this.httpClient
      .get<any>(`${base}${suffix}`, {
        observe: 'response',
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map((response: HttpResponse<any>) => {
          const body = response.body;
          const raw = body?.value ?? body?.data ?? body ?? [];
          const arr = Array.isArray(raw) ? raw : [];
          const items = arr.map((item: any) => {
            const candidate = RecruitmentCandidateModel.create(item);
            Object.keys(item || {}).forEach(key => {
              (candidate as any)[key] = item[key];
            });
            return candidate;
          });
          const totalHeader = response.headers.get('X-Count') ?? response.headers.get('x-count');
          const totalCount = totalHeader ? parseInt(totalHeader, 10) : items.length;
          return { items, totalCount: Number.isFinite(totalCount) ? totalCount : items.length };
        }),
        catchError(() => of({ items: [], totalCount: 0 }))
      );
  }

  getTalentBankPreSignedUrlPut(
    companyGroupId: string,
    fileType: 'CURRICULUM' | 'PHOTO' | 'CUSTOM_FIELD',
    fileName: string,
    contentType: string
  ): Observable<{ url: string; fileKey: string }> {
    const params = {
      companyGroupId,
      fileType,
      fileName,
      contentType,
    };

    return this.httpClientNoInterceptors.get<{ url: string; fileKey: string }>(
      `${environment.apiUrl}/${this.endpoint}/talent-bank/pre-signed-url-put`,
      { params }
    );
  }

  transferCandidateToTalentBank(
    recruitmentCandidateId: string,
    payload?: {
      jobTitleGroupId: string;
      jobTitleId?: string | null;
      observation?: string;
    }
  ): Observable<any> {
    if (!recruitmentCandidateId) {
      throw new Error('RecruitmentCandidateId é obrigatório');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/candidates/${recruitmentCandidateId}/transfer-to-talent-bank`;

    return this.httpClient.put<any>(url, payload ?? {}).pipe(
      map((response: any) => {
        if (!response) return {};
        return response.data || response.body || response;
      }),
      catchError(error => {
        console.error('❌ Erro ao transferir candidato para banco de talentos:', error);
        throw error;
      })
    );
  }

  removeCandidateFromTalentBank(recruitmentCandidateId: string): Observable<any> {
    if (!recruitmentCandidateId) {
      throw new Error('RecruitmentCandidateId é obrigatório');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/candidates/${recruitmentCandidateId}/remove-from-talent-bank`;

    return this.httpClient.put<any>(url, {}).pipe(
      map((response: any) => {
        if (!response) return {};
        return response.data || response.body || response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  importCandidatesToTalentBank(recruitmentId: string, candidateIds: string[]): Observable<any> {
    if (!recruitmentId) {
      throw new Error('RecruitmentId é obrigatório');
    }

    if (!candidateIds || candidateIds.length === 0) {
      throw new Error('Pelo menos um CandidateId é obrigatório');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/${recruitmentId}/import-multiple-candidates`;
    const payload = {
      candidateIds,
    };

    return this.httpClient.post<any>(url, payload).pipe(
      catchError(error => {
        throw error;
      })
    );
  }

  withdrawCandidate(
    recruitmentId: string,
    candidateId: string,
    payload: {
      recruitWithdrawalReasonId: string;
      commentary: string;
    }
  ): Observable<any> {
    if (!recruitmentId || !candidateId) {
      throw new Error('RecruitmentId e CandidateId são obrigatórios');
    }

    const url = `${this.baseUrl}/${this.endpoint}/${recruitmentId}/candidates/${candidateId}/withdraw`;

    return this.httpClient.put(url, payload);
  }

  updateCandidateReferral(
    recruitmentId: string,
    candidateId: string,
    payload: {
      referredByEmployeeId?: string | null;
      referredByName?: string | null;
    }
  ): Observable<any> {
    if (!recruitmentId || !candidateId) {
      throw new Error('RecruitmentId e CandidateId são obrigatórios');
    }

    const url = `${this.baseUrl}/${this.endpoint}/${recruitmentId}/candidates/${candidateId}/referral`;
    return this.httpClient.put(url, payload);
  }

  updateCandidatePipelineStage(
    candidateId: string,
    recruitmentId: string,
    pipelineStageId: string,
    position?: number
  ): Observable<any> {
    if (!candidateId || !recruitmentId || !pipelineStageId) {
      throw new Error('Parâmetros inválidos para atualização de pipeline');
    }

    const uri = `${this.baseUrl}/${this.endpoint}/candidates/${candidateId}/pipeline-stage/${pipelineStageId}?recruitmentId=${encodeURIComponent(recruitmentId)}`;
    const payload = position !== undefined ? { order: position } : {};

    return this.httpClient.put(uri, payload).pipe(
      map(response => response),
      catchError(error => {
        throw error;
      })
    );
  }

  resendPreRegistrationLink(recruitmentId: string, candidateId: string): Observable<void> {
    if (!recruitmentId || !candidateId) {
      throw new Error('RecruitmentId e CandidateId são obrigatórios');
    }

    const url = `${this.baseUrl}/${this.endpoint}/${recruitmentId}/candidates/${candidateId}/pre-registration/resend`;

    return this.httpClient.post<void>(url, {}).pipe(map(() => undefined));
  }

  updateCandidatePipelineStageDefaultApproval(
    candidateId: string,
    recruitmentId: string,
    position?: number
  ): Observable<any> {
    if (!candidateId || !recruitmentId) {
      throw new Error('Parâmetros inválidos para atualização de pipeline');
    }

    const uri = `${this.baseUrl}/${this.endpoint}/candidates/${candidateId}/approve-candidate?recruitmentId=${encodeURIComponent(recruitmentId)}`;
    const payload = position !== undefined ? { order: position } : {};

    return this.httpClient.put(uri, payload).pipe(
      map(response => response),
      catchError(error => {
        throw error;
      })
    );
  }

  moveCandidatesToPipelineStage(
    recruitmentId: string,
    candidateIds: string[],
    pipelineStageId: string
  ): Observable<any> {
    if (!recruitmentId) {
      throw new Error('RecruitmentId não fornecido');
    }
    if (!candidateIds || candidateIds.length === 0) {
      throw new Error('CandidateIds não fornecidos');
    }
    if (!pipelineStageId) {
      throw new Error('PipelineStageId não fornecido');
    }

    const uri = `${this.endpoint}/candidates/move-to-pipeline-stage`;
    const payload = {
      recruitmentId,
      candidateIds,
      pipelineStageId,
    };

    return this.http.put(uri, payload, true).pipe(
      map(response => response),
      catchError(error => {
        throw error;
      })
    );
  }

  getClosedPipelineStageIds(recruitmentId: string): Observable<string[]> {
    if (!recruitmentId) {
      throw new Error('RecruitmentId não fornecido');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/${recruitmentId}/pipeline-stages/closed`;

    return this.httpClient
      .get<any>(url, { context: new HttpContext().set(BYPASS_LOG, true) })
      .pipe(
        map(res => res?.data || res?.body || res || []),
        map((ids: any) => (Array.isArray(ids) ? ids : [])),
        map(ids => ids.map((x: any) => String(x).trim()).filter((x: string) => x)),
        catchError(() => of([]))
      );
  }

  closePipelineStage(payload: RecruitmentCloseStagePayload): Observable<any> {
    if (!payload?.recruitmentId) {
      throw new Error('RecruitmentId não fornecido');
    }
    if (!payload?.pipelineStageId) {
      throw new Error('PipelineStageId não fornecido');
    }

    const uri = `${this.endpoint}/pipeline-stages/close`;

    const body = {
      recruitmentId: payload.recruitmentId,
      pipelineStageId: payload.pipelineStageId,
      candidateIds: Array.isArray(payload.candidateIds) ? payload.candidateIds : [],
      recruitRejectionReasonId: payload.recruitRejectionReasonId,
      commentary: payload.commentary,
    };

    return this.http.put(uri, body, true).pipe(
      map(response => response),
      catchError(error => {
        throw error;
      })
    );
  }

  reopenPipelineStage(payload: RecruitmentReopenStagePayload): Observable<any> {
    if (!payload?.recruitmentId) {
      throw new Error('RecruitmentId não fornecido');
    }
    if (!payload?.pipelineStageId) {
      throw new Error('PipelineStageId não fornecido');
    }

    const uri = `${this.endpoint}/pipeline-stages/reopen`;
    const body = {
      recruitmentId: payload.recruitmentId,
      pipelineStageId: payload.pipelineStageId,
    };

    return this.http.put(uri, body, true).pipe(
      map(response => response),
      catchError(error => {
        throw error;
      })
    );
  }

  scheduleCandidateInterview(
    recruitmentId: string,
    candidateId: string,
    payload: ScheduleInterviewPayload
  ): Observable<void> {
    if (!recruitmentId || !candidateId) {
      throw new Error('RecruitmentId e candidateId são obrigatórios');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/${recruitmentId}/candidates/${candidateId}/schedule`;

    // HttpClient instead of HttpService: HttpService.post serializes the body as a JSON string
    // and ASP.NET [FromBody] cannot bind RecruitCandidateScheduleViewModel from a string token.
    return this.httpClient.post<void>(url, payload, {
      context: new HttpContext()
        .set(BYPASS_LOG, true)
        .set(SKIP_ERROR_INTERCEPTOR, true),
    });
  }

  schedulePipelineStageInterviews(
    recruitmentId: string,
    pipelineStageId: string,
    payload: ScheduleInterviewPayload
  ): Observable<void> {
    if (!recruitmentId || !pipelineStageId) {
      throw new Error('RecruitmentId e pipelineStageId são obrigatórios');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/${recruitmentId}/pipeline-stages/${pipelineStageId}/schedule`;

    return this.httpClient.post<void>(url, payload, {
      context: new HttpContext()
        .set(BYPASS_LOG, true)
        .set(SKIP_ERROR_INTERCEPTOR, true),
    });
  }

  downloadCandidateResume(candidateId: string): Observable<Blob> {
    if (!candidateId) {
      throw new Error('CandidateId não fornecido');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/resume/${candidateId}/download`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  downloadCustomFieldFile(candidateId: string, customFieldItemId: string): Observable<Blob> {
    if (!candidateId || !customFieldItemId) {
      throw new Error('CandidateId e CustomFieldItemId são obrigatórios');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/candidates/${candidateId}/custom-fields/${customFieldItemId}/download`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  rejectCandidate(
    recruitmentId: string,
    candidateId: string,
    payload: {
      qualifiedJobTitleGroupId: string | null;
      qualifiedJobTitleId?: string | null;
      recruitRejectionReasonId: string;
      commentary: string;
      sendRejectionEmail: boolean;
    }
  ): Observable<any> {
    if (!recruitmentId || !candidateId) {
      throw new Error('RecruitmentId e CandidateId são obrigatórios');
    }

    const url = `${this.baseUrl}/${this.endpoint}/${recruitmentId}/candidates/${candidateId}/reject`;

    return this.httpClient.put(url, payload);
  }

  listReassignmentTargetRecruitments(
    recruitmentId: string,
    candidateId: string
  ): Observable<RecruitmentModel[]> {
    if (!recruitmentId || !candidateId) {
      throw new Error('recruitmentId e candidateId são obrigatórios');
    }
    const url = `${this.baseUrl}/${this.endpoint}/${recruitmentId}/candidates/${candidateId}/reassignment/target-recruitments`;
    return this.httpClient.get<any>(url).pipe(
      map((res: any) => {
        const raw = res?.value ?? res?.body ?? res ?? [];
        const arr = Array.isArray(raw) ? raw : [];
        return arr.map((item: any) => RecruitmentModel.create(item));
      }),
      catchError(() => of([]))
    );
  }

  inviteReassignment(
    recruitmentId: string,
    candidateId: string,
    payload: RecruitmentReassignmentInviteModel
  ): Observable<any> {
    if (!recruitmentId || !candidateId) {
      throw new Error('recruitmentId e candidateId são obrigatórios');
    }
    const url = `${this.baseUrl}/${this.endpoint}/${recruitmentId}/candidates/${candidateId}/reassignment/invite`;
    return this.httpClient.post(url, {
      targetRecruitmentId: payload.targetRecruitmentId,
      recruiterMessage: payload.recruiterMessage ?? '',
    });
  }

  getReassignmentPreviewPublic(token: string): Observable<RecruitmentReassignmentPreviewModel> {
    const enc = encodeURIComponent(token);
    const url = `${environment.apiUrl}/${this.endpoint}/page/reassignment/${enc}/preview`;
    return this.httpClientNoInterceptors
      .get<any>(url, {
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(map(body => RecruitmentReassignmentPreviewModel.create(body ?? {})));
  }

  getReassignmentDetailsPublic(token: string): Observable<RecruitmentReassignmentDetailsModel> {
    const enc = encodeURIComponent(token);
    const url = `${environment.apiUrl}/${this.endpoint}/page/reassignment/${enc}/details`;
    return this.httpClientNoInterceptors
      .get<any>(url, {
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(map(body => RecruitmentReassignmentDetailsModel.create(body ?? {})));
  }

  verifyReassignmentOtpPublic(token: string, otp: string): Observable<any> {
    const enc = encodeURIComponent(token);
    const url = `${environment.apiUrl}/${this.endpoint}/page/reassignment/${enc}/verify-otp`;
    return this.httpClientNoInterceptors.post(
      url,
      { otp },
      { context: new HttpContext().set(BYPASS_LOG, true) }
    );
  }

  acceptReassignmentPublic(token: string): Observable<any> {
    const enc = encodeURIComponent(token);
    const url = `${environment.apiUrl}/${this.endpoint}/page/reassignment/${enc}/accept`;
    return this.httpClientNoInterceptors.post(
      url,
      {},
      { context: new HttpContext().set(BYPASS_LOG, true) }
    );
  }

  rejectReassignmentPublic(token: string): Observable<any> {
    const enc = encodeURIComponent(token);
    const url = `${environment.apiUrl}/${this.endpoint}/page/reassignment/${enc}/reject`;
    return this.httpClientNoInterceptors.post(
      url,
      {},
      { context: new HttpContext().set(BYPASS_LOG, true) }
    );
  }

  getPreregistrationContext(recruitmentId: string, candidateId: string): Observable<any> {
    const url = `${environment.apiUrl}/${this.endpoint}/page/preregistration/${recruitmentId}/${candidateId}`;
    return this.httpClientNoInterceptors.get<any>(url);
  }

  submitPreregistration(payload: any): Observable<any> {
    const url = `${environment.apiUrl}/${this.endpoint}/page/preregistration/submit`;
    return this.httpClientNoInterceptors.post<any>(url, payload);
  }

  getPreSignedUrlForCustomField(
    recruitmentId: string,
    fileName: string,
    contentType: string
  ): Observable<{ url: string; fileKey: string }> {
    const params = {
      fileName: fileName,
      contentType: contentType,
      recruitmentId: recruitmentId,
    };

    return this.httpClientNoInterceptors.get<{ url: string; fileKey: string }>(
      `${environment.apiUrl}/${this.endpoint}/custom-fields/pre-signed-url-put`,
      { params }
    );
  }

  getPreSignedUrlForAdmissionField(
    recruitmentId: string,
    fileName: string,
    contentType: string
  ): Observable<{ url: string; fileKey: string }> {
    const params = {
      fileName: fileName,
      contentType: contentType,
      recruitmentId: recruitmentId,
    };

    return this.httpClientNoInterceptors.get<{ url: string; fileKey: string }>(
      `${environment.apiUrl}/${this.endpoint}/admission-custom-fields/pre-signed-url-put`,
      { params }
    );
  }

  uploadToS3(url: string, file: File, contentType?: string): Observable<any> {
    const resolvedContentType = (contentType || file?.type || 'application/octet-stream').trim();

    const headers = new HttpHeaders({
      'Content-Type': resolvedContentType,
    });

    return this.httpClientNoInterceptors.put(url, file, {
      headers: headers,
      reportProgress: true,
      observe: 'events',
    });
  }

  private createCandidateWithPosition(
    input: any,
    recruitmentId: string
  ): RecruitmentCandidateWithPositionModel {
    const candidateData = input?.candidate || input?.recruitmentCandidate || input;
    const candidate = RecruitmentCandidateModel.create(candidateData);
    const positions = this.extractPositions(input, recruitmentId);

    return {
      candidate,
      positions,
      activePosition: positions[0] || null,
    };
  }

  private extractPositions(
    input: any,
    recruitmentId: string
  ): RecruitmentCandidatePositionModel[] {
    const rawPositions =
      input?.positions || input?.candidatePositions || input?.recruitmentCandidatePositions || [];
    const parsedPositions = Array.isArray(rawPositions)
      ? rawPositions.map((position: any) => RecruitmentCandidatePositionModel.create(position))
      : [];

    const filtered = parsedPositions.filter(position => position.recruitmentId === recruitmentId);
    return filtered.length ? filtered : parsedPositions;
  }
}
